import * as R from 'remeda';
import queryString from 'query-string';
import { of, throwError } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { z } from 'zod';
import { AnnotationPayload } from '@rossum/api-client/annotations';
import { Page } from '@rossum/api-client/pages';
import { AnnotationStatus } from '@rossum/api-client/shared';
import { isEmbedded } from '../../../constants/config';
import { allTabStatuses } from '../../../features/document-list-columns/status/helpers';
import {
  DataGridConditionModel,
  filteringSchema,
} from '../../../features/mql/encodeMqlQuery';
import { asArray, asScalar } from '../../../lib/url';
import { SnakeCaseStatus } from '../../../types/annotationStatus';
import { Url } from '../../../types/basic';
import { parseAndValidate } from '../../../utils/jsonParse';
import { DOCUMENTS_QUERY_FOR_BROWSER_TAB } from '../localStorage/actions';
import { alertNetworkOutage, leaveValidation } from '../ui/actions';
import {
  confirmAnnotationFulfilled,
  deleteAnnotation,
  displayAnnotation,
  nextAnnotation,
  postponeAnnotation,
  rejectAnnotationFulfilled,
  startAnnotationFulfilled,
} from './actions';
import { AnnotationLoadMode } from './types';

export type AnnotationAction =
  | 'upload'
  | 'validate'
  | 'reprocess'
  | 'reprocessConfirmed'
  | 'postpone'
  | 'move'
  | 'label'
  | 'delete'
  | 'purge'
  | 'reExtract'
  | 'reAutomate'
  | 'addAttachment'
  | 'createRelation'
  | 'toggleTraining'
  | 'moveToReview'
  | 'export'
  | 'documentHistory';

export const dataDisabled: SnakeCaseStatus[] = ['failed_import', 'importing'];

export const reprocessable: SnakeCaseStatus[] = ['failed_export'];

const reAutomatable: SnakeCaseStatus[] = ['to_review', 'postponed'];

// Re-extract supports the broader pre-MAT-1552 set; only re-automate was narrowed.
const reExtractable: SnakeCaseStatus[] = [
  'failed_export',
  'confirmed',
  'exported',
  'postponed',
  'to_review',
  'split',
  'rejected',
  'failed_import',
];
const purgeable: SnakeCaseStatus[] = ['deleted'];

const deletable: SnakeCaseStatus[] = [
  'exported',
  'exporting',
  'failed_export',
  'failed_import',
  'importing',
  'postponed',
  'reviewing',
  'to_review',
  'rejected',
  'confirmed',
];

const attachable: SnakeCaseStatus[] = [
  'exported',
  'exporting',
  'failed_export',
  'in_workflow',
  'to_review',
  'reviewing',
  'postponed',
  'rejected',
  'confirmed',
  'split',
];

const postponable: SnakeCaseStatus[] = ['to_review', 'reviewing', 'confirmed'];

export const reviewable: SnakeCaseStatus[] = [
  'to_review',
  'reviewing',
  'postponed',
  'confirmed',
];

/**
 * A `reviewing` document is held open by the user who is validating it, so
 * restarting its processing is reserved for that user; all other statuses are
 * decided by the status lists alone.
 *
 * `modifier` and `currentUserUrl` are only known in the document view — the
 * dashboard omits them, which keeps `reviewing` documents off limits there.
 */
export type ReprocessingParams = {
  status: AnnotationStatus;
  modifier?: Url | null;
  currentUserUrl?: Url;
};

const isReviewedByCurrentUser = ({
  modifier,
  currentUserUrl,
}: ReprocessingParams): boolean => !!modifier && modifier === currentUserUrl;

export const isReAutomatable = (params: ReprocessingParams): boolean =>
  params.status === 'reviewing'
    ? isReviewedByCurrentUser(params)
    : reAutomatable.includes(params.status);

export const isReExtractable = (params: ReprocessingParams): boolean =>
  params.status === 'reviewing'
    ? isReviewedByCurrentUser(params)
    : reExtractable.includes(params.status);

export const isDeletable = (status: SnakeCaseStatus): boolean =>
  deletable.includes(status);

export const isPurgeable = (status: SnakeCaseStatus): boolean =>
  purgeable.includes(status);

export const isReprocessable = (status: SnakeCaseStatus): boolean =>
  reprocessable.includes(status);

export const isPostponable = (status: SnakeCaseStatus): boolean =>
  postponable.includes(status);

export const isReviewable = (status: SnakeCaseStatus): boolean =>
  reviewable.includes(status);

// the condition for attachments and downloadability seem to share the same list of statuses, hence we use the same function.
export const canHaveAttachments = (status: SnakeCaseStatus): boolean =>
  attachable.includes(status);

export const handleAnnotationStatus =
  (id: number, mode: AnnotationLoadMode) => (error: XMLHttpRequest) => {
    // When the annotation is purged, pretend it does not exist.
    if (error.status === 409 && error.response.annotation_status === 'purged')
      return throwError({ status: 404 });

    if ([403, 409].includes(error.status))
      return of(displayAnnotation({ id, mode }));
    return throwError(error);
  };

type DataShapeT = {
  status: SnakeCaseStatus;
  pages: Array<string | Page>;
  restrictedAccess: boolean;
};

export const hasDataAvailable = ({
  status,
  pages,
  restrictedAccess,
}: DataShapeT) => {
  return !(
    restrictedAccess === true ||
    dataDisabled.includes(status) ||
    (status === 'deleted' && pages.length === 0)
  );
};

export const validationEndingActionCreators = [
  confirmAnnotationFulfilled,
  postponeAnnotation,
  deleteAnnotation,
  leaveValidation,
  nextAnnotation,
  rejectAnnotationFulfilled,
];

export const readOnlyModeEndingActionCreators = [
  leaveValidation,
  nextAnnotation,
  startAnnotationFulfilled,
  alertNetworkOutage,
];

const parseAllDocsQuery = (query: string): DataGridConditionModel => {
  const { filtering } = queryString.parse(query, { arrayFormat: 'none' });
  // try to parse stored query and validate it against schema
  // return default shape if it is not valid in catch
  return parseAndValidate(asScalar(filtering ?? '') ?? null, filteringSchema, {
    items: [],
  });
};

const filterStatuses = (status: string | string[] | null) =>
  reviewable.filter(_status => asArray(status).includes(_status));

type CombinedStatuses = { is: string[]; not: string[] };

const combineStatuses = (
  acc: CombinedStatuses,
  key: keyof CombinedStatuses,
  value: string
) => ({
  ...acc,
  [key]: R.unique(acc[key].concat(value).flat()),
});

const resolveStatusesForAllDocumentsDashboard = () => {
  // The value must be read from session storage, since the value in localStorage
  // can be affected by opening the dashboard in a different browser tab
  const cachedDashboardQuery =
    sessionStorage.getItem(DOCUMENTS_QUERY_FOR_BROWSER_TAB) || '';
  const parsed = parseAllDocsQuery(cachedDashboardQuery);

  const statusFilters = parsed.items.filter(({ field }) => field === 'status');

  // split statuses based on operators;
  // combine `is` and `isAnyOf` into `is` field
  // combine `not` and `notIn` into `not` field
  const statuses = statusFilters.reduce<CombinedStatuses>(
    (acc, cur) =>
      combineStatuses(
        acc,
        cur.operator === 'not' || cur.operator === 'notIn' ? 'not' : 'is',
        cur.value
      ),
    { is: [], not: [] }
  );

  // decide which statuses are visible on dashboard, in case statuses.is is an empty array, fallback to allTabStatuses
  // filter those statuses that are included in statuses.not and should be hidden on dashboard
  const statusesToFilter = (
    statuses.is.length ? statuses.is : allTabStatuses
  ).filter(_status => !statuses.not.includes(_status));

  return filterStatuses(statusesToFilter);
};

/**
 * The user starts reviewing annotation from a particular tab on the dashboard.
 * When starting an annotation (via /start or /next endpoint),
 * we need to ensure that the annotation still has the expected status
 * (status which belongs to that tab). Otherwise, the user can repeatedly review
 * annotation which was already postponed or moved to confirmed status.
 */
export const getReviewableStatusesFromAnnotationsQuery =
  (): AnnotationStatus[] => {
    // The annotations query local storage is not valid for embedded mode
    if (isEmbedded()) return [];
    /**
     * Force status to "reviewing" here.
     * In some cases (e.g. page refresh) the cancel request may not have completed yet,
     * leading to a 409 Conflict when starting the annotation.
     * Adding reviewing status ensures we don’t hit that race condition.
     */
    return R.unique([
      ...resolveStatusesForAllDocumentsDashboard(),
      'reviewing' as AnnotationStatus,
    ]);
  };

const missingAnnotationErrorSchema = z
  .object({
    annotations: z.record(z.array(z.string())),
  })
  .nullable()
  .catch(null);

export const getMissingAnnotationIndexes = (error: AjaxError) => {
  const parsedResponse = missingAnnotationErrorSchema.parse(error.response);
  if (!parsedResponse) return null;

  const missingAnnotationIndexes = Object.entries(parsedResponse.annotations)
    .filter(([_, value]) =>
      value.some(errorMessage => errorMessage.includes('Invalid hyperlink'))
    )
    .flatMap(([key]) =>
      !Number.isNaN(parseInt(key, 10)) ? parseInt(key, 10) : []
    );

  return missingAnnotationIndexes;
};

export const buildReExtractPayload = (): AnnotationPayload => ({
  rirPollId: null,
  status: 'importing',
  messages: [],
});
